import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { YearMonth } from '../api/listByStatusRequest'
import { fetchApprovedReservationsForMonth } from '../api/approvedReservationsRequest'
import { ApprovedReservationSlot } from '../availabilityRules'

const monthKey = (month: YearMonth): string => `${month.year}-${month.month}`

const parseMonthKey = (key: string): YearMonth => {
  const [year, month] = key.split('-').map(Number)
  return { year, month }
}

// PLA-3423: reservas aprovadas das salas selecionadas, carregadas mês a mês sob demanda (meses
// visíveis no calendário + meses do período escolhido). Falha de rede não trava o formulário — o
// envio ainda passa pela pré-checagem e pelo 409 do backend.
export const useApprovedReservations = (meetingRoomIds: string[], months: YearMonth[]) => {
  const [slotsByMonth, setSlotsByMonth] = useState<Record<string, ApprovedReservationSlot[]>>({})
  const requestedRef = useRef(new Set<string>())
  const roomsKey = meetingRoomIds.join('|')
  const roomsKeyRef = useRef(roomsKey)
  const monthsKey = Array.from(new Set(months.map(monthKey))).join(',')

  const load = useCallback(async (keys: string[], requestRoomsKey: string): Promise<void> => {
    const roomIds = requestRoomsKey ? requestRoomsKey.split('|') : []
    if (roomIds.length === 0) return
    await Promise.all(
      keys.map(async key => {
        requestedRef.current.add(key)
        try {
          const slots = await fetchApprovedReservationsForMonth(parseMonthKey(key), roomIds)
          if (roomsKeyRef.current !== requestRoomsKey) return
          setSlotsByMonth(prev => ({ ...prev, [key]: slots }))
        } catch {
          requestedRef.current.delete(key)
        }
      })
    )
  }, [])

  useEffect(() => {
    if (roomsKeyRef.current !== roomsKey) {
      roomsKeyRef.current = roomsKey
      requestedRef.current.clear()
      setSlotsByMonth({})
    }
    const missing = monthsKey.split(',').filter(key => key !== '' && !requestedRef.current.has(key))
    if (missing.length > 0) void load(missing, roomsKey)
  }, [roomsKey, monthsKey, load])

  const refetch = useCallback((): void => {
    requestedRef.current.clear()
    void load(monthsKey.split(',').filter(Boolean), roomsKey)
  }, [load, monthsKey, roomsKey])

  const slots = useMemo((): ApprovedReservationSlot[] => {
    const byId = new Map<string, ApprovedReservationSlot>()
    Object.values(slotsByMonth).flat().forEach(slot => byId.set(slot.id, slot))
    return Array.from(byId.values())
  }, [slotsByMonth])

  return { slots, refetch }
}
